/** Selected-expiry / selected-structure state for the workspace.
 * Keeps the header decision aligned with the Options tab. No network access.
 * Production Intelligence may seed expiry from the shared URL contract.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  defaultExpiryDate, deriveWorkspaceDecision, strategyRows,
} from "@/workspace/mockTickerData";
import { liveDecision } from "@/workspace/live/workspaceLiveAdapter";
import { parseExpiry, parseStrategy } from "@/workspace/handoff";

const Ctx = createContext(null);

export function WorkspaceSelectionProvider({ ticker, children, initialExpiry, initialStrategy }) {
  const urlExpiry = parseExpiry(initialExpiry);
  const initialResolved = ticker.live
    ? (urlExpiry || ticker.defaultExpiryDate || ticker.availableExpirations?.[0]?.date || null)
    : defaultExpiryDate();
  const [selectedExpiry, setSelectedExpiry] = useState(initialResolved);
  const [selectedStrategy, setSelectedStrategy] = useState(null);

  useEffect(() => {
    const nextExpiry = ticker.live
      ? (parseExpiry(initialExpiry) || ticker.defaultExpiryDate || ticker.availableExpirations?.[0]?.date || null)
      : defaultExpiryDate();
    setSelectedExpiry(nextExpiry);
    setSelectedStrategy(ticker.live && ticker.optionStructure ? ticker.optionStructure.strategy : null);
  }, [ticker.ticker, ticker.live, ticker.defaultExpiryDate, initialExpiry]);

  const rows = useMemo(
    () => (ticker.live
      ? (ticker.optionStructure ? [ticker.optionStructure] : [])
      : strategyRows(ticker.ticker, selectedExpiry)),
    [ticker, selectedExpiry],
  );
  const lookup = parseStrategy(initialStrategy);
  const selectedRow = rows.find((r) => r.strategy === selectedStrategy)
    || (lookup ? rows.find((r) => r.strategy === lookup || r.id === lookup) : null)
    || (ticker.live ? rows[0] || null : null);
  const decision = useMemo(
    () => (ticker.live
      ? liveDecision(ticker, selectedExpiry)
      : deriveWorkspaceDecision(ticker, selectedExpiry, selectedRow)),
    [ticker, selectedExpiry, selectedRow],
  );

  // Historical-research selection. Deliberately separate from selectedRow:
  // picking a research strategy never produces a current structure, contract,
  // leg set, premium or product action.
  const researchRows = ticker.live ? (ticker.researchRows || []) : [];
  const selectedResearchStrategyId = ticker.live
    ? (ticker.researchFocus?.requestedId || ticker.researchFocus?.strategyId || null)
    : null;
  const selectedResearchRow = selectedResearchStrategyId
    ? researchRows.find((r) => r.strategy_id === selectedResearchStrategyId) || null
    : null;

  const value = useMemo(
    () => ({
      selectedExpiry,
      setSelectedExpiry,
      selectedStrategy,
      setSelectedStrategy,
      selectedRow,
      rows,
      decision,
      researchRows,
      selectedResearchStrategyId,
      selectedResearchRow,
      researchFocus: ticker.live ? ticker.researchFocus || null : null,
    }),
    [selectedExpiry, selectedStrategy, selectedRow, rows, decision, researchRows, selectedResearchStrategyId, selectedResearchRow, ticker],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWorkspaceSelection() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useWorkspaceSelection requires WorkspaceSelectionProvider");
  return ctx;
}
